import { logger } from "@/util/logger";
import Loader from "@/Components/Loader";
import {
  EMPTY_AUDIO,
  getAppMeta,
  getAppOnlyRedirectUrl,
  getBase64fromUrl,
  getClientInfo,
} from "@/helper/helper";
import { getProfileDetailsApi } from "@/services/axios/protectedApi";
import { magicLogin } from "@/services/axios/unProtectedApi";
import { Howl } from "howler";
import { useRouter } from "next/router";
import { useEffect, useRef } from "react";
import { toaster } from "@/Components/toast";
import { usePathname } from "next/navigation";
import { useThemeProvider } from "@/contexts/ThemeContext";
import { useAuthStore } from "@/contexts/AuthContext";
import i18n, { isSupportedLanguageCode } from "@/src/i18n";
import { syncOnboardingTourPending } from "@/Components/OnboardingTour/tourStorage";

const Home = () => {
  const router = useRouter();
  const pathname = usePathname();

  const { updateTheme } = useThemeProvider();
  const { updateSessionFromStorage, getDefaultLandingRoute } = useAuthStore();

  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;
    const queryParams = new URLSearchParams(window.location.search);
    const magicToken = queryParams.get("token"); // for email and password based login
    const emailTokenLogin = queryParams.get("token-login"); // for email based login
    const isImpersonationLogin = queryParams.get("impersonate") === "true";
    const isAdminImpersonation =
      queryParams.get("adminImpersonation") === "true";
    const trainee = queryParams.get("trainee"); // for view as trainee option
    const trainingId = queryParams.get("bookId");
    const tId = queryParams.get("tId");
    const orgLogo = queryParams.get("orgLogo");
    const syncAdminImpersonation = () => {
      if (isAdminImpersonation) {
        sessionStorage.setItem("isAdminImpersonation", "true");
        sessionStorage.setItem(
          "impersonatedUserName",
          queryParams.get("impersonatedUserName") || ""
        );
      } else {
        sessionStorage.removeItem("isAdminImpersonation");
        sessionStorage.removeItem("impersonatedUserName");
      }
      window.setTimeout(
        () => window.dispatchEvent(new Event("admin-impersonation-ready")),
        0
      );
    };

    const checkLoggedIn = async () => {
      try {
        if (emailTokenLogin) {
          localStorage.clear();
          localStorage.setItem("token", emailTokenLogin);
          if (isImpersonationLogin) {
            localStorage.setItem("isImpersonationLogin", "true");
          }
          syncAdminImpersonation();
          let profileData: any = null;
          try {
            const updateLastActivity = localStorage.getItem("orgName") !== null;
            const profileRes = await getProfileDetailsApi(updateLastActivity);
            profileData = profileRes?.data?.data;

            if (profileData) {
              await i18n.changeLanguage(
                isSupportedLanguageCode(profileData.preferredLanguage)
                  ? profileData.preferredLanguage
                  : "en"
              );
              localStorage.setItem("email", profileData.email || "");
              localStorage.setItem(
                "orgName",
                profileData?.org?.organizationName || ""
              );
              localStorage.setItem("userId", profileData._id || "");
              localStorage.setItem("role", profileData.role || "");
              if (profileData.org) {
                localStorage.setItem("org", JSON.stringify(profileData.org));
                updateTheme(profileData.org.theme || {});
                if (profileData.org.logoUrl) {
                  try {
                    const base64String = await getBase64fromUrl(
                      profileData.org.logoUrl
                    );
                    localStorage.setItem("orgLogo", base64String);
                  } catch (logoErr) {
                    console.error("Error setting orgLogo base64:", logoErr);
                  }
                }
              }
              // Update AuthContext reactively so all layout/sidebar/dashboard elements update immediately
              updateSessionFromStorage();

              // First-run tour: never-active, non-manager user. Not for
              // impersonated sessions.
              syncOnboardingTourPending({
                lastActivity: profileData.lastActivity,
                role: profileData.role,
                isImpersonated: isImpersonationLogin || isAdminImpersonation,
              });
            }
            const appMeta = getAppMeta();
            if (profileData?.org?.appOnlyAccess && !appMeta) {
              const redirectUrl = getAppOnlyRedirectUrl(
                profileData.org.appOnlyAccess,
                profileData.role,
                localStorage.getItem("token") || ""
              );
              if (redirectUrl) {
                localStorage.clear();
                window.location.href = redirectUrl;
                return;
              }
            }
          } catch (error) {
            console.error("Error setting customFeatureNames:", error);
          }
          await router.push(
            getDefaultLandingRoute(undefined, profileData?.org)
          );
          return; // Ensure no further logic executes after this point
        }

        if (
          magicToken &&
          trainee &&
          !pathname?.includes("/link/") &&
          !pathname?.includes("roleplay-demo") &&
          !pathname?.includes("share-session") &&
          !pathname?.includes("pitch-partner")
        ) {
          const tokenString = Array.isArray(magicToken)
            ? magicToken[0]
            : magicToken;

          localStorage.clear();
          localStorage.setItem("token", tokenString);
          syncAdminImpersonation();
          if (orgLogo) {
            const base64String = await getBase64fromUrl(
              decodeURIComponent(orgLogo)
            );
            localStorage.setItem("orgLogo", base64String);
          }

          await router.push(getDefaultLandingRoute());
          return;
        }

        if (
          magicToken &&
          !pathname?.includes("/link/") &&
          !pathname?.includes("roleplay-demo") &&
          !pathname?.includes("share-session") &&
          !pathname?.includes("pitch-partner")
        ) {
          try {
            localStorage.clear();
            const userAgent = getClientInfo();

            const res = await magicLogin({ token: magicToken, userAgent });

            if (res?.data?.passwordResetRequired) {
              await router.push({
                pathname: "/changePass",
                query: {
                  email: res.data.email,
                  activationToken: res.data.activationToken,
                },
              });
              toaster.success(
                res?.data?.message || "Please set a new password to continue."
              );
              return;
            }

            if (res?.data?.token) {
              await i18n.changeLanguage(
                isSupportedLanguageCode(res.data.preferredLanguage)
                  ? res.data.preferredLanguage
                  : "en"
              );
              localStorage.setItem("token", res.data.token);
              // First-run tour: the API returns the user's lastActivity from
              // before this login; null means never active. Managers excluded.
              syncOnboardingTourPending({
                lastActivity: res.data.lastActivity,
                role: res.data?.data?.role || res.data?.role,
                isImpersonated: isImpersonationLogin || isAdminImpersonation,
              });
              localStorage.setItem(
                "allowSelfAssignEvaluation",
                res.data?.allowSelfAssignEvaluation || false
              );
              localStorage.setItem("email", res.data.email);
              localStorage.setItem("orgName", res.data?.orgName || "");
              localStorage.setItem(
                "org",
                JSON.stringify(res?.data?.data?.org || res?.data?.org || null)
              );

              updateTheme(res?.data?.data?.org?.theme || {});

              const appMeta = getAppMeta();
              const orgData = res?.data?.data?.org || res?.data?.org;
              const roleData = res?.data?.data?.role || res?.data?.role;
              if (orgData?.appOnlyAccess && !appMeta) {
                const redirectUrl = getAppOnlyRedirectUrl(
                  orgData?.appOnlyAccess,
                  roleData,
                  localStorage.getItem("token") || ""
                );

                if (redirectUrl) {
                  localStorage.clear();
                  window.location.href = redirectUrl;
                  return; // stop login flow
                }
              }

              if (res.data?.orgLogo) {
                const base64String = await getBase64fromUrl(res.data?.orgLogo);
                localStorage.setItem("orgLogo", base64String);
              }
              localStorage.setItem("botIcon", res.data?.globalBotData?.botIcon);
              if (res.data?.globalBotData?.name) {
                localStorage.setItem("botName", res.data?.globalBotData?.name);
              }

              let redirectTo: string = "";

              // Priority 1: Check for explicit redirectTo parameter
              const redirectToParam = queryParams.get("redirectTo");
              if (redirectToParam) {
                redirectTo = redirectToParam;
              } else if (tId) {
                redirectTo = `/home/training?tId=${tId}`;
              } else {
                redirectTo = trainingId?.length
                  ? `/home/training?id=${trainingId}`
                  : getDefaultLandingRoute(roleData, orgData);
              }
              await router.push(redirectTo);

              return; // Ensure no further logic executes
            } else if (res?.data?.otpUnVerfied) {
              toaster.error("Invalid magic token or login failed.");
              console.error(res?.data.message);
              await router.push({
                pathname: "/verification",
                query: { email: res.data.email },
              });
              return;
            } else {
              toaster.error("Invalid magic token or login failed.");
            }
          } catch (error) {
            logger.log("error", error);
            toaster.error("Magic token expired or invalid");
            localStorage.clear();
            void router.push("/signin");
            return;
          }
        }

        // const jwtToken = localStorage.getItem("token");
        // if (jwtToken) {
        const pathName = window.location.pathname + window.location.search;
        await router.push(pathName === "/" ? "/signin" : pathName);
        // } else {
        //   // Redirect to signin if no token is found or magic login fails
        //   await router.push("/signin");
        // }
      } catch (error) {
        console.error("Error in checkLoggedIn function:", error);
        toaster.error("An unexpected error occurred. Please try again.");
      }
    };

    void checkLoggedIn();
  }, [router]);

  // required to play first audio on user click / mobile support
  useEffect(() => {
    const audioSrc = `data:audio/mpeg;base64,${EMPTY_AUDIO}`;
    new Howl({
      src: [audioSrc],
    }).play();
  }, []);

  return <Loader />;
};

export default Home;
